import React, { createContext, useContext, useReducer } from "react";

import {
  OPEN_DROPDOWN,
  CHECK_OPTION,
  SHOW_ALL_CLICK,
  CLOSE_DROPDOWN,
  CHECK_PILL_CAPTURE,
  CLEAR_FILTERS,
  SHOW_ALL_CAPTURES,
  UNCHECK_OPTION,
} from "@constants/filter-constants";

import analyticsDispatcher from "@components/CreateRoom/analyticsDispatcher";

import { reducer, initialState } from "@reducers/FilterReducer";
import useEvent from "@hooks/useEvent";

export const FilterContext = createContext({});

const FilterProvider = ({ children }) => {
  const [state, dispatchStateUnwrapped] = useReducer(reducer, initialState);
  const { dispatch } = useEvent();

  const dispatchState = (action) => {
    analyticsDispatcher(dispatch)(state)(action);
    dispatchStateUnwrapped(action);
  };

  return (
    <FilterContext.Provider value={[state, dispatchState]}>
      {children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;

export function useFilterContext() {
  const [state, dispatchState] = useContext(FilterContext);
  return [state, dispatchState];
}

export const handleCheckOption = (dispatch) => (filterProp) => (key) => {
  if (key === CHECK_PILL_CAPTURE) {
    handleCaptureClick(dispatch);
  }

  dispatch({
    type: CHECK_OPTION,
    payload: {
      filterProp,
      key,
    },
  });
};

export const handleShowAll = (dispatch) => (filterProp) => (key) => {
  if (key === CHECK_PILL_CAPTURE) {
    handleCaptureClick(dispatch);
  }

  dispatch({
    type: SHOW_ALL_CLICK,
    payload: {
      filterProp,
      key,
    },
  });
};

export const handleUncheckOption = (dispatch) => (filterProp) => (key) => {
  if (key === CHECK_PILL_CAPTURE) {
    handleCaptureClick(dispatch);
  }

  dispatch({
    type: UNCHECK_OPTION,
    payload: {
      filterProp,
      key,
    },
  });
};

export const handleDropdownOpen = (dispatch, activePill) => {
  dispatch({
    type: OPEN_DROPDOWN,
    payload: activePill,
  });
};

export const handleDropdownClose = (dispatch, filterProp) => {
  dispatch({
    type: CLOSE_DROPDOWN,
    payload: filterProp,
  });
};

export const handleCaptureClick = (dispatch) => {
  dispatch({
    type: CHECK_PILL_CAPTURE,
  });
};

export const handleClearFilters = (dispatch) => {
  dispatch({
    type: CLEAR_FILTERS,
  });
};

export const handleShowAllCapturesClick = (dispatch) => {
  dispatch({
    type: SHOW_ALL_CAPTURES,
  });
};

export const getRoomsListBySceneIds = (rooms, ids = []) =>
  rooms.filter((room) => ids.includes(room.sceneId));

export const getRoomsListByCompositionIds = (rooms, ids = []) =>
  rooms.filter((room) => ids.includes(room.compositionId));

export const ROOM_SHELVES = [
  {
    heading: "Furnished rooms",
    filterProp: "furnishingType",
    filterKey: "PARTLY_FURNISHED",
  },
];

const TEST_DATA_US_EN = {
  firstRowDashboard: [12868, 12200, 12219, 10695],
  firstRow: [12868, 12200, 12219, 13165],
  secondRow: [50181, 50177, 49693, 49691],
  thirdRow: [49246, 49242, 49241, 49238],
};

const TEST_DATA_CA_EN = {
  firstRowDashboard: [16650, 17142],
  secondRow: TEST_DATA_US_EN.secondRow,
  thirdRow: TEST_DATA_US_EN.thirdRow,
};

const TEST_DATA_CA_FR = {
  firstRowDashboard: [16650, 17143],
  secondRow: TEST_DATA_US_EN.secondRow,
  thirdRow: TEST_DATA_US_EN.thirdRow,
};

const PROD_DATA_US_EN = {
  firstRowDashboard: [27035, 27212, 27193, 27091],
  firstRow: [16099, 16100, 15305, 11400],
  secondRow: [35267, 35266, 35265, 45184],
  thirdRow: [31312, 31313, 31078, 28539],
};

const PROD_DATA_CA_EN = {
  firstRowDashboard: [60053, 60050, 60049, 60045],
  secondRow: PROD_DATA_US_EN.secondRow,
  thirdRow: PROD_DATA_US_EN.thirdRow,
};

const PROD_DATA_CA_FR = {
  firstRowDashboard: [60141, 60137, 60133, 60131],
  secondRow: PROD_DATA_US_EN.secondRow,
  thirdRow: PROD_DATA_US_EN.thirdRow,
};

const ROOM_MAP =
  process.env.GATSBY_ENV === "production" ? (
    process.env.GATSBY_IKEA_MARKET === "ca/en" ? PROD_DATA_CA_EN :
    process.env.GATSBY_IKEA_MARKET === "ca/fr" ? PROD_DATA_CA_FR :
    PROD_DATA_US_EN
  ) : (
    process.env.GATSBY_IKEA_MARKET === "ca/en" ? TEST_DATA_CA_EN :
    process.env.GATSBY_IKEA_MARKET === "ca/fr" ? TEST_DATA_CA_FR :
    TEST_DATA_US_EN
  );

export const firstRoomShelve = (rooms, overrideMap = "firstRow") =>
  getRoomsListByCompositionIds(rooms, ROOM_MAP[overrideMap]);

export const secondRoomShelve = (rooms) =>
  getRoomsListBySceneIds(rooms, ROOM_MAP.secondRow);

export const thirdRoomShelve = (rooms) =>
  getRoomsListBySceneIds(rooms, ROOM_MAP.thirdRow);
